import * as THREE from 'three';
import { HAIR_COLORS, PALETTE, SKIN_TONES, type CharacterConfig } from '@kokun/shared';
import { canvasTexture, disposeObject, ownToon, toon } from '../core/materials';

/**
 * Avatar estilizado construido con primitivas (sin assets externos: licencia 100 % propia).
 * El modelo mira hacia −Z con rotation.y = 0, igual que la física compartida.
 *
 * Rostro: un casquete esférico delante de la cabeza recibe una textura de canvas. Esa textura
 * es la foto procesada del jugador (con bordes difuminados que se funden con la piel) o una
 * cara dibujada según la expresión. Es una proyección: funciona bien de frente y en 3/4,
 * y se deforma algo de perfil (limitación documentada).
 */

export type AnimState = 'idle' | 'walk' | 'run' | 'jump' | 'fall' | 'stunned' | 'carry' | 'celebrate' | `emote:${string}`;

interface Rig {
  root: THREE.Group;
  body: THREE.Group;
  torso: THREE.Mesh;
  head: THREE.Group;
  armL: THREE.Group;
  armR: THREE.Group;
  legL: THREE.Group;
  legR: THREE.Group;
  face: THREE.Mesh;
  propeller?: THREE.Object3D;
  stars: THREE.Group;
}

const BODY = {
  classic: { torso: [0.62, 0.7, 0.42], leg: 0.55, arm: 0.55, head: 0.46, width: 0.2 },
  chunky: { torso: [0.86, 0.72, 0.56], leg: 0.48, arm: 0.6, head: 0.44, width: 0.28 },
  mini: { torso: [0.5, 0.52, 0.36], leg: 0.42, arm: 0.42, head: 0.5, width: 0.16 },
} as const;

/** Dibuja una cara de dibujos animados según la expresión. */
export function drawExpression(ctx: CanvasRenderingContext2D, expression: string, size = 256) {
  ctx.clearRect(0, 0, size, size);
  const s = size / 256;
  ctx.fillStyle = '#1b1b2f';
  ctx.strokeStyle = '#1b1b2f';
  ctx.lineCap = 'round';
  ctx.lineWidth = 9 * s;
  const eye = (x: number, y: number, r: number) => {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(x * s, y * s, r * 1.15 * s, r * 1.4 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1b1b2f';
    ctx.beginPath();
    ctx.ellipse(x * s, (y + 4) * s, r * 0.62 * s, r * 0.8 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc((x + 4) * s, (y - 2) * s, r * 0.22 * s, 0, Math.PI * 2);
    ctx.fill();
  };
  const brows = (tilt: number) => {
    ctx.beginPath();
    ctx.moveTo(78 * s, (88 - tilt) * s);
    ctx.lineTo(112 * s, (88 + tilt) * s);
    ctx.moveTo(144 * s, (88 + tilt) * s);
    ctx.lineTo(178 * s, (88 - tilt) * s);
    ctx.stroke();
  };
  ctx.fillStyle = 'rgba(255,120,140,0.35)';
  ctx.beginPath();
  ctx.ellipse(70 * s, 160 * s, 18 * s, 10 * s, 0, 0, Math.PI * 2);
  ctx.ellipse(186 * s, 160 * s, 18 * s, 10 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  switch (expression) {
    case 'neutral':
      eye(95, 118, 17);
      eye(161, 118, 17);
      ctx.beginPath();
      ctx.moveTo(105 * s, 178 * s);
      ctx.lineTo(151 * s, 178 * s);
      ctx.stroke();
      break;
    case 'mischief':
      eye(95, 120, 15);
      eye(161, 120, 15);
      brows(-8);
      ctx.beginPath();
      ctx.moveTo(95 * s, 170 * s);
      ctx.quadraticCurveTo(140 * s, 200 * s, 165 * s, 162 * s);
      ctx.stroke();
      break;
    case 'surprised':
      eye(95, 115, 20);
      eye(161, 115, 20);
      ctx.fillStyle = '#1b1b2f';
      ctx.beginPath();
      ctx.ellipse(128 * s, 182 * s, 14 * s, 18 * s, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'angry':
      eye(95, 122, 15);
      eye(161, 122, 15);
      brows(10);
      ctx.beginPath();
      ctx.moveTo(100 * s, 186 * s);
      ctx.quadraticCurveTo(128 * s, 166 * s, 156 * s, 186 * s);
      ctx.stroke();
      break;
    default: {
      eye(95, 118, 17);
      eye(161, 118, 17);
      ctx.fillStyle = '#1b1b2f';
      ctx.beginPath();
      ctx.moveTo(92 * s, 166 * s);
      ctx.quadraticCurveTo(128 * s, 214 * s, 164 * s, 166 * s);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#ff6b81';
      ctx.beginPath();
      ctx.ellipse(128 * s, 186 * s, 14 * s, 7 * s, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

const geo = {
  sphere: new THREE.SphereGeometry(1, 20, 16),
  sphereLow: new THREE.SphereGeometry(1, 10, 8),
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(1, 1, 1, 14),
  cone: new THREE.ConeGeometry(1, 1, 14),
  torus: new THREE.TorusGeometry(1, 0.18, 8, 20),
};

function mesh(g: THREE.BufferGeometry, m: THREE.Material, s: [number, number, number], p: [number, number, number] = [0, 0, 0], shadow = true) {
  const o = new THREE.Mesh(g, m);
  o.scale.set(...s);
  o.position.set(...p);
  o.castShadow = shadow;
  return o;
}

function stripes(color: number, stripe: number) {
  return canvasTexture(64, 64, (c) => {
    c.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    c.fillRect(0, 0, 64, 64);
    c.fillStyle = `#${stripe.toString(16).padStart(6, '0')}`;
    for (let y = 0; y < 64; y += 16) c.fillRect(0, y, 64, 7);
  });
}

function hawaii(color: number) {
  return canvasTexture(128, 128, (c) => {
    c.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    c.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 14; i++) {
      const x = (i * 53) % 128;
      const y = (i * 37) % 128;
      c.fillStyle = i % 2 ? '#fff3b0' : '#ff8fab';
      for (let p = 0; p < 5; p++) {
        c.beginPath();
        c.arc(x + Math.cos((p / 5) * 6.28) * 6, y + Math.sin((p / 5) * 6.28) * 6, 5, 0, Math.PI * 2);
        c.fill();
      }
      c.fillStyle = '#ffd60a';
      c.beginPath();
      c.arc(x, y, 3, 0, Math.PI * 2);
      c.fill();
    }
  });
}

export class Avatar {
  readonly root = new THREE.Group();
  private rig!: Rig;
  private cfg!: CharacterConfig;
  private faceCanvas = document.createElement('canvas');
  private faceTexture: THREE.CanvasTexture;
  private faceImage: ImageBitmap | HTMLCanvasElement | null = null;
  private tintables: THREE.MeshToonMaterial[] = [];
  private headBaseScale = 1;
  private headScaleCurrent = 1;
  private t = Math.random() * 10;
  private landSquash = 0;
  private carryItem: THREE.Object3D | null = null;
  state: AnimState = 'idle';
  speed = 0;
  bigHead = false;
  paintColor: number | null = null;
  ghost = false;

  constructor(cfg: CharacterConfig) {
    this.faceCanvas.width = this.faceCanvas.height = 256;
    this.faceTexture = new THREE.CanvasTexture(this.faceCanvas);
    this.faceTexture.colorSpace = THREE.SRGBColorSpace;
    this.build(cfg);
  }

  get config() {
    return this.cfg;
  }

  /** Reconstruye el modelo con una nueva configuración (editor de personajes, cambios en sala). */
  build(cfg: CharacterConfig) {
    if (this.rig) {
      this.root.remove(this.rig.root);
      disposeObject(this.rig.root);
    }
    this.cfg = cfg;
    this.tintables = [];
    const b = BODY[cfg.body as keyof typeof BODY] ?? BODY.classic;
    const skin = SKIN_TONES[cfg.skin] ?? SKIN_TONES[0];
    const shirtColor = PALETTE[cfg.shirtColor] ?? PALETTE[0];
    const pants = PALETTE[cfg.pantsColor] ?? PALETTE[6];
    const shoes = PALETTE[cfg.shoesColor] ?? PALETTE[9];
    const hairColor = HAIR_COLORS[cfg.hairColor] ?? HAIR_COLORS[0];

    const mat = (c: number) => {
      const m = ownToon(c);
      this.tintables.push(m);
      return m;
    };
    const skinM = mat(skin);
    let shirtM: THREE.MeshToonMaterial;
    if (cfg.shirt === 'stripes') {
      shirtM = mat(0xffffff);
      shirtM.map = stripes(shirtColor, 0xffffff);
    } else if (cfg.shirt === 'hawaii') {
      shirtM = mat(0xffffff);
      shirtM.map = hawaii(shirtColor);
    } else shirtM = mat(cfg.shirt === 'suit' ? 0x2b2d42 : shirtColor);
    const pantsM = mat(pants);
    const shoesM = mat(shoes);
    const hairM = mat(hairColor);

    const root = new THREE.Group();
    const body = new THREE.Group();
    root.add(body);
    const [tw, th, td] = b.torso;
    const hipY = b.leg + 0.08;

    // Piernas (pivote en la cadera)
    const leg = (side: number) => {
      const g = new THREE.Group();
      g.position.set(side * b.width, hipY, 0);
      g.add(mesh(geo.cyl, pantsM, [0.13, b.leg, 0.13], [0, -b.leg / 2, 0]));
      g.add(mesh(geo.sphere, shoesM, [0.16, 0.1, 0.22], [0, -b.leg, -0.05]));
      body.add(g);
      return g;
    };
    const legL = leg(-1);
    const legR = leg(1);

    // Torso redondeado
    const torso = mesh(geo.sphere, shirtM, [tw / 1.7, th / 1.6, td / 1.5], [0, hipY + th / 2, 0]);
    body.add(torso);
    body.add(mesh(geo.sphere, pantsM, [tw / 1.8, 0.16, td / 1.6], [0, hipY + 0.06, 0]));
    if (cfg.shirt === 'suit') {
      body.add(mesh(geo.box, mat(0xffffff), [0.16, th * 0.7, 0.05], [0, hipY + th * 0.58, -td / 1.55]));
      body.add(mesh(geo.box, mat(0xe63946), [0.06, th * 0.5, 0.06], [0, hipY + th * 0.55, -td / 1.5]));
    }
    if (cfg.shirt === 'hoodie') body.add(mesh(geo.torus, shirtM, [0.26, 0.26, 0.5], [0, hipY + th + 0.02, 0.12]));

    // Brazos (pivote en el hombro)
    const arm = (side: number) => {
      const g = new THREE.Group();
      g.position.set(side * (tw / 1.7 + 0.04), hipY + th * 0.85, 0);
      g.add(mesh(geo.cyl, shirtM, [0.1, b.arm, 0.1], [0, -b.arm / 2, 0]));
      g.add(mesh(geo.sphere, skinM, [0.12, 0.12, 0.12], [0, -b.arm - 0.04, 0]));
      body.add(g);
      return g;
    };
    const armL = arm(-1);
    const armR = arm(1);

    // Cabeza
    const head = new THREE.Group();
    const r = b.head;
    head.position.set(0, hipY + th + r * 0.85, 0);
    body.add(head);
    head.add(mesh(geo.sphere, skinM, [r, r * 0.98, r]));
    head.add(mesh(geo.sphere, skinM, [r * 0.18, r * 0.22, r * 0.12], [-r * 0.98, 0, 0]));
    head.add(mesh(geo.sphere, skinM, [r * 0.18, r * 0.22, r * 0.12], [r * 0.98, 0, 0]));

    // Casquete frontal para el rostro (−Z). phi 1.1π..1.9π cubre el frente.
    const faceGeo = new THREE.SphereGeometry(r * 1.012, 32, 24, Math.PI * 1.08, Math.PI * 0.84, Math.PI * 0.18, Math.PI * 0.62);
    const faceMat = new THREE.MeshToonMaterial({ map: this.faceTexture, transparent: true, gradientMap: undefined, depthWrite: false });
    const face = new THREE.Mesh(faceGeo, faceMat);
    face.renderOrder = 2;
    head.add(face);

    // Pelo
    const hr = r * 1.04;
    switch (cfg.hair) {
      case 'spiky':
        for (let i = 0; i < 7; i++) {
          const a = (i / 7) * Math.PI * 2;
          const c = mesh(geo.cone, hairM, [0.12, 0.3, 0.12], [Math.cos(a) * r * 0.5, r * 0.85, Math.sin(a) * r * 0.5 + r * 0.1]);
          c.rotation.set(Math.sin(a) * 0.6, 0, -Math.cos(a) * 0.6);
          head.add(c);
        }
        head.add(mesh(geo.sphere, hairM, [hr, hr * 0.55, hr], [0, r * 0.35, r * 0.06]));
        break;
      case 'bob':
        // Melena desplazada hacia atrás: enmarca la cara sin taparla
        head.add(mesh(geo.sphere, hairM, [hr * 1.06, hr * 0.95, hr * 1.02], [0, r * 0.14, r * 0.36]));
        break;
      case 'afro':
        head.add(mesh(geo.sphere, hairM, [hr * 1.35, hr * 1.2, hr * 1.15], [0, r * 0.5, r * 0.55]));
        break;
      case 'mohawk':
        for (let i = 0; i < 5; i++) head.add(mesh(geo.box, hairM, [0.08, 0.28 - Math.abs(i - 2) * 0.04, 0.16], [0, r * 0.95, -r * 0.5 + i * r * 0.28]));
        break;
      case 'ponytail':
        head.add(mesh(geo.sphere, hairM, [hr, hr * 0.6, hr], [0, r * 0.33, r * 0.06]));
        head.add(mesh(geo.sphere, hairM, [0.14, 0.3, 0.14], [0, 0, r * 1.15]));
        break;
      case 'curly':
        for (let i = 0; i < 12; i++) {
          const a = (i / 12) * Math.PI * 2;
          head.add(mesh(geo.sphereLow, hairM, [0.14, 0.14, 0.14], [Math.cos(a) * r * 0.75, r * 0.65, Math.sin(a) * r * 0.75 + r * 0.15]));
        }
        head.add(mesh(geo.sphere, hairM, [hr * 0.9, hr * 0.55, hr * 0.9], [0, r * 0.45, r * 0.1]));
        break;
    }

    // Sombreros
    let propeller: THREE.Object3D | undefined;
    const hatY = r * (cfg.hair === 'afro' ? 1.35 : 0.9);
    const hatColor = PALETTE[(cfg.shirtColor + 3) % PALETTE.length];
    switch (cfg.hat) {
      case 'cap': {
        const capM = mat(hatColor);
        head.add(mesh(geo.sphere, capM, [hr, hr * 0.55, hr], [0, r * 0.4, 0]));
        head.add(mesh(geo.cyl, capM, [r * 0.6, 0.03, r * 0.5], [0, r * 0.42, -r * 0.85]));
        break;
      }
      case 'beanie':
        head.add(mesh(geo.sphere, mat(hatColor), [hr * 1.03, hr * 0.6, hr * 1.03], [0, r * 0.55, 0]));
        head.add(mesh(geo.sphere, mat(0xffffff), [0.1, 0.1, 0.1], [0, r * 1.18, 0]));
        break;
      case 'tophat':
        head.add(mesh(geo.cyl, mat(0x1b1b2f), [r * 0.95, 0.04, r * 0.95], [0, hatY, 0]));
        head.add(mesh(geo.cyl, mat(0x1b1b2f), [r * 0.6, r * 0.9, r * 0.6], [0, hatY + r * 0.45, 0]));
        head.add(mesh(geo.cyl, mat(0xe63946), [r * 0.62, r * 0.12, r * 0.62], [0, hatY + r * 0.1, 0]));
        break;
      case 'cone':
        head.add(mesh(geo.cone, mat(0xff7b00), [r * 0.7, r * 1.6, r * 0.7], [0, hatY + r * 0.7, 0]));
        head.add(mesh(geo.cyl, mat(0xffffff), [r * 0.45, r * 0.15, r * 0.45], [0, hatY + r * 0.7, 0]));
        break;
      case 'propeller': {
        const capM = mat(0x3a86ff);
        head.add(mesh(geo.sphere, capM, [hr, hr * 0.55, hr], [0, r * 0.4, 0]));
        const p = new THREE.Group();
        p.position.set(0, r * 1.05, 0);
        p.add(mesh(geo.box, mat(0xff006e), [r * 1.6, 0.02, 0.1]));
        p.add(mesh(geo.box, mat(0xffbe0b), [0.1, 0.02, r * 1.6]));
        head.add(p);
        propeller = p;
        break;
      }
      case 'chef':
        head.add(mesh(geo.cyl, mat(0xffffff), [r * 0.7, r * 0.6, r * 0.7], [0, hatY + r * 0.2, 0]));
        head.add(mesh(geo.sphere, mat(0xffffff), [r * 0.85, r * 0.5, r * 0.85], [0, hatY + r * 0.65, 0]));
        break;
      case 'crown': {
        const gold = mat(0xffd60a);
        gold.emissive.setHex(0x3a2a00);
        head.add(mesh(geo.cyl, gold, [r * 0.62, r * 0.3, r * 0.62], [0, hatY + r * 0.1, 0]));
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          head.add(mesh(geo.cone, gold, [0.07, 0.18, 0.07], [Math.cos(a) * r * 0.55, hatY + r * 0.35, Math.sin(a) * r * 0.55]));
        }
        break;
      }
    }

    // Gafas (delante: −Z)
    const gz = -r * 0.95;
    const gy = r * 0.08;
    switch (cfg.glasses) {
      case 'round': {
        const fm = mat(0x1b1b2f);
        head.add(mesh(geo.torus, fm, [r * 0.2, r * 0.2, r * 0.2], [-r * 0.33, gy, gz]));
        head.add(mesh(geo.torus, fm, [r * 0.2, r * 0.2, r * 0.2], [r * 0.33, gy, gz]));
        break;
      }
      case 'shades':
        head.add(mesh(geo.box, mat(0x111111), [r * 1.3, r * 0.26, 0.05], [0, gy, gz - 0.02]));
        break;
      case 'star': {
        const sm = mat(0xff006e);
        for (const sx of [-1, 1]) {
          const shape = new THREE.Shape();
          for (let i = 0; i < 10; i++) {
            const rr = i % 2 ? 0.09 : 0.2;
            const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
            if (i === 0) shape.moveTo(Math.cos(a) * rr, Math.sin(a) * rr);
            else shape.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
          }
          const star = new THREE.Mesh(new THREE.ExtrudeGeometry(shape, { depth: 0.04, bevelEnabled: false }), sm);
          star.position.set(sx * r * 0.36, gy, gz - 0.04);
          head.add(star);
        }
        break;
      }
      case 'monocle':
        head.add(mesh(geo.torus, mat(0xffd60a), [r * 0.22, r * 0.22, r * 0.22], [r * 0.33, gy, gz]));
        break;
    }

    // Mochilas (detrás: +Z)
    const backZ = td / 1.5 + 0.05;
    const backY = hipY + th * 0.55;
    switch (cfg.backpack) {
      case 'classic':
        body.add(mesh(geo.box, mat(0xe76f51), [tw * 0.8, th * 0.75, 0.22], [0, backY, backZ]));
        break;
      case 'shell':
        body.add(mesh(geo.sphere, mat(0x2a9d8f), [tw * 0.6, th * 0.6, 0.25], [0, backY, backZ]));
        break;
      case 'jetpack':
        for (const sx of [-1, 1]) body.add(mesh(geo.cyl, mat(0xadb5bd), [0.11, th * 0.8, 0.11], [sx * 0.13, backY, backZ]));
        body.add(mesh(geo.cone, mat(0xffbe0b), [0.08, 0.2, 0.08], [0.13, backY - th * 0.5, backZ]));
        body.add(mesh(geo.cone, mat(0xffbe0b), [0.08, 0.2, 0.08], [-0.13, backY - th * 0.5, backZ]));
        break;
      case 'balloon':
        body.add(mesh(geo.cyl, mat(0xffffff), [0.01, 1.2, 0.01], [0.15, backY + 0.8, backZ]));
        body.add(mesh(geo.sphere, mat(0xff006e), [0.3, 0.36, 0.3], [0.15, backY + 1.6, backZ]));
        break;
    }

    // Accesorios
    switch (cfg.accessory) {
      case 'mustache':
        head.add(mesh(geo.sphere, mat(0x3d2b1f), [r * 0.35, r * 0.09, 0.06], [0, -r * 0.28, -r * 0.97]));
        break;
      case 'duck': {
        const y = mat(0xffd60a);
        head.add(mesh(geo.sphere, y, [0.16, 0.13, 0.18], [0, r + 0.12 + (cfg.hat !== 'none' ? 0.3 : 0), 0]));
        head.add(mesh(geo.sphere, y, [0.1, 0.1, 0.1], [0, r + 0.27 + (cfg.hat !== 'none' ? 0.3 : 0), -0.1]));
        head.add(mesh(geo.cone, mat(0xff7b00), [0.04, 0.08, 0.04], [0, r + 0.26 + (cfg.hat !== 'none' ? 0.3 : 0), -0.2]));
        break;
      }
      case 'banana': {
        const ban = mesh(new THREE.TorusGeometry(0.16, 0.05, 6, 12, Math.PI * 0.9), mat(0xffe066), [1, 1, 1], [r * 1.05, r * 0.2, 0]);
        ban.rotation.y = Math.PI / 2;
        head.add(ban);
        break;
      }
      case 'fish': {
        const f = mesh(geo.sphere, mat(0x4cc9f0), [0.08, 0.1, 0.24], [0, -b.arm - 0.1, -0.1]);
        armR.add(f);
        break;
      }
    }

    // Estrellas de aturdimiento
    const stars = new THREE.Group();
    stars.position.y = hipY + th + r * 2.1;
    for (let i = 0; i < 3; i++) {
      const s = mesh(geo.sphereLow, toon(0xffd60a, { emissive: 0x806000 }), [0.08, 0.08, 0.08], [Math.cos(i * 2.1) * 0.4, 0, Math.sin(i * 2.1) * 0.4], false);
      stars.add(s);
    }
    stars.visible = false;
    body.add(stars);

    root.scale.setScalar(1);
    this.root.add(root);
    this.rig = { root, body, torso, head, armL, armR, legL, legR, face, propeller, stars };
    this.headBaseScale = cfg.headScale;
    head.scale.setScalar(cfg.headScale);
    this.refreshFace();
    this.applyTint();
  }

  /** Establece (o quita) la imagen del rostro fotográfico. */
  setFaceImage(img: ImageBitmap | HTMLCanvasElement | null) {
    this.faceImage = img;
    this.refreshFace();
  }

  private refreshFace() {
    const ctx = this.faceCanvas.getContext('2d')!;
    if (this.faceImage && this.cfg.useFace) {
      ctx.clearRect(0, 0, 256, 256);
      ctx.drawImage(this.faceImage, 0, 0, 256, 256);
    } else drawExpression(ctx, this.cfg.expression);
    this.faceTexture.needsUpdate = true;
  }

  private applyTint() {
    for (const m of this.tintables) {
      m.emissive.setHex(this.paintColor ?? 0x000000);
      m.emissiveIntensity = this.paintColor !== null ? 0.55 : 0;
      m.transparent = this.ghost;
      m.opacity = this.ghost ? 0.4 : 1;
    }
  }

  setPaint(color: number | null) {
    if (color === this.paintColor) return;
    this.paintColor = color;
    this.applyTint();
  }

  setGhost(ghost: boolean) {
    if (ghost === this.ghost) return;
    this.ghost = ghost;
    this.applyTint();
  }

  /** Muestra un objeto (célula de energía) sobre la cabeza. */
  setCarry(obj: THREE.Object3D | null) {
    if (this.carryItem) this.rig.body.remove(this.carryItem);
    this.carryItem = obj;
    if (obj) {
      obj.position.set(0, this.rig.head.position.y + 0.9, 0);
      this.rig.body.add(obj);
    }
  }

  land(speed: number) {
    this.landSquash = Math.min(0.35, speed / 40);
  }

  /** Animación procedural según el estado. */
  update(dt: number) {
    const R = this.rig;
    this.t += dt;
    const t = this.t;
    const st = this.state;
    const k = Math.min(1, dt * 12);
    const lerp = (o: THREE.Object3D, x: number, z = 0) => {
      o.rotation.x += (x - o.rotation.x) * k;
      o.rotation.z += (z - o.rotation.z) * k;
    };
    let legA = 0;
    let armLX = 0;
    let armRX = 0;
    let armLZ = 0.12;
    let armRZ = -0.12;
    let bodyY = 0;
    let bodyRotZ = 0;
    let bodyRotX = 0;
    let headX = 0;

    if (st === 'walk' || st === 'run' || st === 'carry') {
      const f = st === 'run' ? 13 : 9;
      const amp = st === 'run' ? 0.9 : 0.6;
      legA = Math.sin(t * f) * amp;
      armLX = -legA * 0.9;
      armRX = legA * 0.9;
      bodyY = Math.abs(Math.sin(t * f)) * 0.06;
      bodyRotX = st === 'run' ? -0.15 : -0.05;
    } else if (st === 'jump') {
      legA = 0.4;
      armLX = armRX = -2.6;
      armLZ = 0.5;
      armRZ = -0.5;
    } else if (st === 'fall') {
      armLX = -2.4 + Math.sin(t * 20) * 0.5;
      armRX = -2.4 + Math.cos(t * 20) * 0.5;
      armLZ = 0.9;
      armRZ = -0.9;
      legA = Math.sin(t * 16) * 0.4;
    } else if (st === 'stunned') {
      bodyRotZ = Math.sin(t * 9) * 0.25;
      armLZ = 1.2;
      armRZ = -1.2;
      headX = Math.sin(t * 7) * 0.2;
    } else if (st === 'celebrate') {
      bodyY = Math.abs(Math.sin(t * 7)) * 0.5;
      armLX = armRX = -2.9;
      armLZ = 0.4 + Math.sin(t * 14) * 0.2;
      armRZ = -0.4 - Math.sin(t * 14) * 0.2;
    } else if (st.startsWith('emote:')) {
      const e = st.slice(6);
      if (e === 'wave') {
        armRX = -2.8;
        armRZ = -0.3 + Math.sin(t * 10) * 0.45;
      } else if (e === 'dance') {
        bodyRotZ = Math.sin(t * 6) * 0.25;
        bodyY = Math.abs(Math.sin(t * 6)) * 0.12;
        armLX = -1.5 + Math.sin(t * 6) * 1.2;
        armRX = -1.5 - Math.sin(t * 6) * 1.2;
        legA = Math.sin(t * 6) * 0.3;
      } else if (e === 'laugh') {
        headX = -0.35;
        bodyRotX = 0.1 + Math.sin(t * 25) * 0.05;
        armLX = armRX = -0.4;
        armLZ = 0.6;
        armRZ = -0.6;
      } else if (e === 'facepalm') {
        armRX = -2.3;
        armRZ = 0.5;
        headX = 0.35;
      } else if (e === 'flex') {
        armLX = armRX = -1.5;
        armLZ = 1.4 + Math.sin(t * 8) * 0.15;
        armRZ = -1.4 - Math.sin(t * 8) * 0.15;
        bodyRotX = -0.1;
      } else if (e === 'chicken') {
        armLZ = 0.4 + Math.abs(Math.sin(t * 14)) * 0.9;
        armRZ = -0.4 - Math.abs(Math.sin(t * 14)) * 0.9;
        headX = Math.sin(t * 14) * 0.25;
        legA = Math.sin(t * 7) * 0.4;
        bodyY = Math.abs(Math.sin(t * 7)) * 0.08;
      }
    } else {
      bodyY = Math.sin(t * 2) * 0.02;
      armLZ = 0.12 + Math.sin(t * 2) * 0.04;
      armRZ = -armLZ;
    }
    if (this.carryItem) {
      armLX = armRX = -2.9;
      armLZ = 0.2;
      armRZ = -0.2;
    }

    lerp(R.legL, legA);
    lerp(R.legR, -legA);
    lerp(R.armL, armLX, armLZ);
    lerp(R.armR, armRX, armRZ);
    R.body.position.y += (bodyY - R.body.position.y) * k;
    R.body.rotation.z += (bodyRotZ - R.body.rotation.z) * k;
    R.body.rotation.x += (bodyRotX - R.body.rotation.x) * k;
    R.head.rotation.x += (headX - R.head.rotation.x) * k;

    // Aterrizaje: aplastamiento elástico
    this.landSquash = Math.max(0, this.landSquash - dt * 1.6);
    const sq = this.landSquash;
    R.root.scale.set(1 + sq * 0.5, 1 - sq, 1 + sq * 0.5);

    // Cabeza gigante con rebote
    const targetHead = this.headBaseScale * (this.bigHead ? 2.3 : 1);
    this.headScaleCurrent += (targetHead - this.headScaleCurrent) * Math.min(1, dt * 6);
    R.head.scale.setScalar(this.headScaleCurrent * (this.bigHead ? 1 + Math.sin(t * 8) * 0.04 : 1));

    R.stars.visible = st === 'stunned';
    if (R.stars.visible) R.stars.rotation.y += dt * 6;
    if (R.propeller) R.propeller.rotation.y += dt * (this.speed > 1 ? 25 : 6);
    if (this.carryItem) this.carryItem.rotation.y += dt * 2;
  }

  get headWorldY() {
    return this.rig.head.position.y + 0.5 * this.headScaleCurrent;
  }

  dispose() {
    disposeObject(this.root);
    this.faceTexture.dispose();
  }
}
